import { KoobMapMarker, KoobMapCard } from '@koob/maps';
import { KoobButton } from '@koob/ui';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { MUTED_PIN_COLOR, koobMarker } from '@koob/map-marker';
import { ExperienceCurrencyMismatch, ProductTypeEnum } from '@koob/enums';
import { formatCurrency, tripFactsheetUrl } from 'utils';
import { useSelectedType, useTripFilters } from '@koob/trip-designer';
import { useSnack } from '../../hooks';
import { useExperienceSelection } from './ExperienceCard';

const typeIcon = {
  Activity: 'fa-person-hiking',
  AIR: 'fa-plane',
  LAND: 'fa-car',
  RAIL: 'fa-train',
  BOAT: 'fa-ship',
  Program: 'fa-route',
};

const typeLabelKey = {
  Activity: 'types.activity',
  Program: 'types.program',
  Transfer: 'types.transfer',
};

const ComposeAction = ({ experience, mode, onSelect, compatibility }) => {
  const { t } = useTranslation('experiences');
  const { notify } = useSnack();
  const selectedType = useSelectedType();
  const { isSelectedExperienceInTrip, isBelowMinPax } = useExperienceSelection(
    experience,
    mode,
  );

  if (compatibility === 'incompatible') {
    return (
      <span className="text-xs font-medium text-red-600">
        {t('incompatible')}
      </span>
    );
  }

  return (
    <KoobButton
      size="sm"
      variant={compatibility === 'bestMatch' ? 'success' : 'primary'}
      onClick={() => {
        if (experience?.requested) {
          onSelect(experience);
        } else {
          notify(t('requestConnectionToAdd'), { type: 'error' });
        }
      }}
      disabled={
        isSelectedExperienceInTrip ||
        isBelowMinPax ||
        selectedType !== ProductTypeEnum.EXPERIENCE
      }
    >
      {compatibility === 'bestMatch' && (
        <i className="fa-solid fa-shield-check" />
      )}
      {t(
        selectedType === ProductTypeEnum.EXPERIENCE
          ? 'misc:select'
          : 'tripDesigner:compose.selectDay',
      )}
    </KoobButton>
  );
};

const ExperiencePopupCard = ({
  experience,
  mode,
  onSelect,
  compatibility,
  isForTrip,
}) => {
  const { t } = useTranslation('experiences');
  const navigate = useNavigate();
  const tripFilters = useTripFilters();
  const type = experience?.transferType ?? experience?.experienceType;
  const labelKey = typeLabelKey[experience?.experienceType];
  const { durationDays, durationHours, durationMinutes } = experience;
  const hasDuration =
    durationDays > 0 || durationHours > 0 || durationMinutes > 0;
  const hasPrice =
    experience?.price?.price !== undefined &&
    experience?.price?.currency !== ExperienceCurrencyMismatch;

  let action = null;
  if (mode === 'compose') {
    action = (
      <ComposeAction
        experience={experience}
        mode={mode}
        onSelect={onSelect}
        compatibility={compatibility}
      />
    );
  } else if ((!mode || mode === 'list') && !experience?.showcased) {
    // A showcased experience has no detail page for this TO (KD-3873).
    action = (
      <KoobButton
        size="sm"
        variant="primary"
        onClick={() => navigate(`/experiences/${experience?.id}`)}
      >
        {t('seeExperience')}
      </KoobButton>
    );
  }

  return (
    <KoobMapCard
      image={
        experience?.imageUrl ?? experience?.pictures?.[0]?.imageUrl ?? undefined
      }
      badge={
        <>
          <i className={`fa-solid ${typeIcon[type] ?? 'fa-route'}`} />
          {labelKey && t(labelKey)}
        </>
      }
      title={experience?.name}
      href={
        isForTrip
          ? tripFactsheetUrl(`/experiences/${experience?.id}`, {
              locale: tripFilters?.locale ?? 'en',
              tripId: tripFilters?.tripId,
            })
          : undefined
      }
      subtitle={experience?.inCity?.title ?? experience?.startPosition?.name}
      facts={[
        hasDuration && (
          <>
            <i className="fa-light fa-clock" />
            {[
              durationDays > 0 &&
                t('durationUnitOptions.days', { count: durationDays }),
              durationHours > 0 &&
                t('durationUnitOptions.hours', { count: durationHours }),
              durationMinutes > 0 &&
                t('durationUnitOptions.minutes', { count: durationMinutes }),
            ]
              .filter(Boolean)
              .join(' ')}
          </>
        ),
      ]}
      warning={
        experience?.minPax > 1 &&
        t('minimumTravelers', { count: experience?.minPax })
      }
      price={
        hasPrice && (
          <span className="font-semibold text-orange-600">
            {formatCurrency({
              amount: experience?.price?.price,
              currency: experience?.price?.currency,
            })}
          </span>
        )
      }
      action={action}
    />
  );
};

const ExperienceMarker = ({
  experience,
  mode,
  onSelect,
  isForTrip = false,
  compatibility,
  lat,
  lng,
  typeOverride = null,
}) => {
  const latitude = parseFloat(
    '' + (lat ?? experience?.startPosition?.lat ?? experience?.inCity?.lat),
  );
  const longitude = parseFloat(
    '' + (lng ?? experience?.startPosition?.lon ?? experience?.inCity?.lon),
  );
  const type =
    typeOverride ??
    experience.transferType ??
    experience?.experienceType ??
    experience?.type;

  const muted = compatibility === 'incompatible';
  const color = muted ? MUTED_PIN_COLOR : undefined;

  return (
    <KoobMapMarker
      lat={latitude}
      lng={longitude}
      // Pins already on the trip stay filled; incompatible ones are greyed.
      icon={koobMarker(type, { filled: mode === 'itinerary', color })}
      activeIcon={koobMarker(type, { filled: true, color })}
      muted={muted}
      popup={
        <ExperiencePopupCard
          experience={experience}
          mode={mode}
          onSelect={onSelect}
          compatibility={compatibility}
          isForTrip={isForTrip}
        />
      }
    />
  );
};

export default ExperienceMarker;
